import { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { splashCloudAsset } from '../../../data/check-in.js';
import CheckInHeader from '../../check-in/CheckInHeader.js';
import TipBox from '../../check-in/TipBox.js';
import { useTransitionConfig } from './CheckInTransitionContext.js';
import type { CloudRect } from './useCheckInTransition.js';

interface CheckInTransitionOverlayProps {
  cloudRect: CloudRect | null;
  onComplete: () => void;
  onClose?: () => void;
  onReplay?: () => void;
}

/**
 * Full-screen animated overlay that morphs HomeV2Page into CheckInSplashPage.
 *
 * Renders on top of everything (z-40) with initially transparent background.
 * Animates happy-cloud.svg from captured mascot position to center-screen,
 * fades in splash gradient, CheckInHeader, and text.
 */
export default function CheckInTransitionOverlay({
  cloudRect,
  onComplete,
  onClose,
 // onReplay,
}: CheckInTransitionOverlayProps) {
  const { spring, disableAutoNavigate } = useTransitionConfig();
  //const [animationDone, setAnimationDone] = useState(false);
  const completeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (completeTimerRef.current !== null) {
        clearTimeout(completeTimerRef.current);
      }
    };
  }, []);

  const targetCloudSize = 260;
  const headerHeight = 100;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 430;

  // Center cloud in the content area below header
  const contentAreaHeight = viewportHeight - headerHeight;
  const targetTop = headerHeight + (contentAreaHeight - targetCloudSize) / 2 - 86;
  const targetLeft = (viewportWidth - targetCloudSize) / 2;

  // Starting position from captured rect, fallback to target (no movement)
  const startTop = cloudRect?.top ?? targetTop;
  const startLeft = cloudRect?.left ?? targetLeft;
  const startSize = cloudRect?.width ?? targetCloudSize;

  const handleClose = () => {
    if (completeTimerRef.current !== null) {
      clearTimeout(completeTimerRef.current);
      completeTimerRef.current = null;
    }
    if (onClose) onClose();
  };

  const handleCloudAnimationComplete = () => {
    //setAnimationDone(true);
    completeTimerRef.current = setTimeout(handleTextAnimationComplete, 5000);
    //()
  };

  const handleTextAnimationComplete = () => {
    if (!disableAutoNavigate) {
      onComplete();
    }
  };

  return (
    <div
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 40 }}
    >
      {/* Splash background gradient — fades in */}
      <motion.div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to bottom, #00A7C8 21.1%, #FFFFFF 92.1%)',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          type: 'tween',
          duration: 0.5,
          delay: 0.05,
          ease: 'easeInOut',
        }}
      />

      {/* CheckInHeader — fades in */}
      <motion.div
        style={{ position: 'relative' }}
        className="pointer-events-auto"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: 'tween',
          duration: 0.25,
          delay: 0.15,
          ease: 'easeOut',
        }}
      >
        <CheckInHeader onClose={onClose ? handleClose : undefined} />
      </motion.div>

      {/* Cloud — spring animation from captured rect to center */}
      <motion.img
        src={splashCloudAsset}
        alt="Happy cloud"
        style={{ position: 'absolute' }}
        initial={{
          top: startTop,
          left: startLeft,
          width: startSize,
          height: startSize,
        }}
        animate={{
          top: targetTop,
          left: targetLeft,
          width: targetCloudSize,
          height: targetCloudSize,
        }}
        transition={{
          top: {
            ...spring,
            velocity: -2,
          },
          left: spring,
          width: spring,
          height: spring,
        }}
        onAnimationComplete={handleCloudAnimationComplete}
      />

      {/* "hLet's check in togeter!" text — fades in after cloud settles */}
      <motion.div
        className="absolute inset-x-0 flex justify-center"
        style={{
          top: targetTop + targetCloudSize + 12,
        }}
        initial={{ opacity: 0, y: 8 }}
        //animate={animationDone ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: 'tween',
          duration: 0.25,
          delay: 0.4,
          ease: 'easeOut',
        }}
        onAnimationComplete={() => {
          // if (animationDone) {
           //completeTimerRef.current = setTimeout(handleTextAnimationComplete, 150);
          // }
        }}
      >
        <p
          className="text-center text-[#1B2831] font-semibold text-2xl md:text-[26px] lg:text-[28px] max-w-[320px]"
          style={{ fontFamily: 'DM Sans', letterSpacing: '-0.96px' }}
        >
          Let&apos;s check in together!
        </p>
      </motion.div>

      {/* Tip box + Next button — fades in alongside question text */}
      <motion.div
        className="absolute bottom-8 inset-x-0 px-6 flex flex-col gap-4 pointer-events-auto"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: 'tween',
          duration: 0.25,
          delay: 0.45,
          ease: 'easeOut',
        }}
      >
        <div className="max-w-[382px] mx-auto w-full">
          <TipBox>
            When you check in too, you&apos;re showing your child that feelings are normal and manageable. That&apos;s co-regulation in action.
          </TipBox>
        </div>
        <button
          onClick={() => {
            if (completeTimerRef.current !== null) {
              clearTimeout(completeTimerRef.current);
            }
            onComplete();
          }}
          className="w-full max-w-[382px] mx-auto h-12 rounded-xl border-2 border-[#01A7C8] bg-white flex items-center justify-center gap-2.5 hover:bg-[#C7EDEF]/30 transition-colors"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 6L9 17l-5-5" stroke="#007CAD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span
            className="text-xs font-black uppercase text-[#007CAD]"
            style={{ fontFamily: 'DM Sans', letterSpacing: '0.72px' }}
          >
            NEXT
          </span>
        </button>
      </motion.div>

      {/* Dev mode: Replay button */}
      {/* {showReplay && animationDone && (
        <div className="absolute bottom-8 inset-x-0 flex justify-center pointer-events-auto">
          <button
            onClick={onReplay}
            className="bg-black/80 text-white px-6 py-3 rounded-full font-semibold text-sm"
          >
            Replay Animation
          </button>
        </div>
      )} */}
    </div>
  );
}
